import { effect, Injectable, signal, WritableSignal } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { FormControl } from '@angular/forms';
import { toObservable } from '@angular/core/rxjs-interop';

export const SORT_DEFAULT_OPTIONS: Sort = {
  active: 'none',
  direction: '',
};

export enum UiTableKeys {
  ProjectsList = 'projects-list-table',
  CustomerSpaceList = 'customer-space-list-table',
  CustomerSpaceUsers = 'customer-space-users-table',
  PortalUsers = 'portal-users',
  AccessRightsDocumentUsers = 'access-rights-document-users',
  AccessRightsFileProcessUsers = 'access-rights-file-process-users',
  AccessRightsProjectsUsers = 'access-rights-projects-users',
  LibraryPermissionsOverview = 'access-rights-library-permissions-overview',
  DocumentLibraryDocumentList = 'document-library-document-list',
  TaskList = 'task-list',
  DocumentLibraryAdminList = 'document-library-admin-list',
  FileProcessAdditionalFolderList = 'file-process-additional-folder-list',
}

export enum UiSingleOptions {
  FpaFilterUsersByCustomerSpace = 'fpa.filter-user-by-customer-space',
  ClientAdminUpdateEmployeeDialogFilterUsersByCustomerSpace = 'client-admin-update-employee.filter-user-by-customer-space',
  FpaManageAccessUserRoleDialogFilterUsersByCustomerSpace = 'fpa-manage-access.filter-user-by-customer-space',
}

export interface TableOptionsState {
  userTypeFilter: string | null;
  sort: Sort;
  pageIndex: number;
  pageSize: number;
}

export interface TableOptionsStateWithFilter<TFilter extends object> extends TableOptionsState {
  filter: TFilter;
}

const DEFAULT_OPTIONS: TableOptionsState = {
  userTypeFilter: null,
  sort: {
    active: 'none',
    direction: '',
  },
  pageIndex: 0,
  pageSize: 50,
};

@Injectable({
  providedIn: 'root',
})
export class OptionsStoreService {
  getOptionsStore(key: UiTableKeys): IOptionsStore;
  getOptionsStore<TFilter extends object>(key: UiTableKeys, defaultFilter: TFilter): IOptionsStoreWithFilter<TFilter>;
  getOptionsStore<TFilter extends object = object>(
    key: UiTableKeys,
    defaultFilter?: TFilter,
  ): IOptionsStore | IOptionsStoreWithFilter<TFilter> {
    if (defaultFilter) {
      return new OptionsStoreWithFilter(key, defaultFilter);
    } else {
      return new OptionsStore(key);
    }
  }

  patchOptions(key: UiTableKeys, patch: Partial<TableOptionsState>) {
    if (!patch) {
      return;
    }

    const strItem = localStorage.getItem(key);
    if (!strItem) {
      const options = {
        ...DEFAULT_OPTIONS,
        ...patch,
      };
      localStorage.setItem(key, JSON.stringify(options));
      return;
    }

    // fallback
    const optionMaybeOldFormat = JSON.parse(strItem);
    if (optionMaybeOldFormat.active) {
      const options = {
        ...DEFAULT_OPTIONS,
        sort: optionMaybeOldFormat as Sort,
        ...patch,
      };
      localStorage.setItem(key, JSON.stringify(options));
      return;
    }

    const newOptions = {
      ...DEFAULT_OPTIONS,
      ...optionMaybeOldFormat,
      ...patch,
    };
    localStorage.setItem(key, JSON.stringify(newOptions));
  }

  getOptionsOrDefault(key: UiTableKeys): TableOptionsState {
    const strItem = localStorage.getItem(key);
    if (!strItem) {
      return DEFAULT_OPTIONS;
    }

    // fallback
    const optionMaybeOldFormat = JSON.parse(strItem);
    if (optionMaybeOldFormat.active) {
      return {
        ...DEFAULT_OPTIONS,
        sort: optionMaybeOldFormat as Sort,
      };
    }

    return optionMaybeOldFormat as TableOptionsState;
  }

  setOption<T>(key: UiSingleOptions, value: T) {
    localStorage.setItem(key, `${value}`);
  }

  getOptionOrDefault(key: UiSingleOptions, defaultValue: any = '') {
    const strItem = localStorage.getItem(key);
    if (!strItem) {
      return defaultValue;
    }
    if (typeof defaultValue === 'boolean') {
      return strItem.toLowerCase() === 'true';
    }
    return strItem;
  }
}

export interface IOptionsStore {
  registerSort: (sort: MatSort) => void;
  registerPaginatorPageSize: (paginator: MatPaginator) => void;
}

class OptionsStore implements IOptionsStore {
  private readonly options: WritableSignal<TableOptionsState>;

  constructor(key: UiTableKeys) {
    this.options = signal(readStoredOptions(key, DEFAULT_OPTIONS));
    effect(() => {
      localStorage.setItem(key, JSON.stringify(this.options()));
    });
  }

  public registerSort(sort: MatSort) {
    registerSort(sort, this.options);
  }

  public registerPaginatorPageSize(paginator: MatPaginator) {
    registerPaginatorPageSize(paginator, this.options);
  }
}

export interface IOptionsStoreWithFilter<TFilter extends object> extends IOptionsStore {
  registerFilter: <TProp extends keyof TFilter>(filterProp: TProp, filter: FormControl<TFilter[TProp] | null>) => void;
  registerFilterSignal: <TProp extends keyof TFilter>(filterProp: TProp, filter: WritableSignal<TFilter[TProp]>) => void;
}

class OptionsStoreWithFilter<TFilter extends object> implements IOptionsStoreWithFilter<TFilter> {
  private readonly options: WritableSignal<TableOptionsStateWithFilter<TFilter>>;

  constructor(key: UiTableKeys, defaultFilter: TFilter) {
    this.options = signal(
      readStoredOptions(key, {
        ...DEFAULT_OPTIONS,
        filter: defaultFilter,
      }),
    );

    effect(() => {
      localStorage.setItem(key, JSON.stringify(this.options()));
    });
  }

  public registerSort(sort: MatSort) {
    registerSort(sort, this.options);
  }

  public registerPaginatorPageSize(paginator: MatPaginator) {
    registerPaginatorPageSize(paginator, this.options);
  }

  public registerFilter<TProp extends keyof TFilter>(filterProp: TProp, filter: FormControl<TFilter[TProp] | null>) {
    filter.setValue(this.options().filter[filterProp]);
    filter.valueChanges.subscribe((value) =>
      this.options.update((options) => ({ ...options, filter: { ...options.filter, [filterProp]: value } })),
    );
  }

  public registerFilterSignal<TProp extends keyof TFilter>(filterProp: TProp, filter: WritableSignal<TFilter[TProp]>) {
    filter.set(this.options().filter[filterProp]);
    toObservable(filter).subscribe((value) =>
      this.options.update((options) => ({ ...options, filter: { ...options.filter, [filterProp]: value } })),
    );
  }
}

const readStoredOptions = <TOptions extends TableOptionsState>(key: string, defaultOptions: TOptions): TOptions => {
  const storageValue = localStorage.getItem(key);
  if (!storageValue) {
    return defaultOptions;
  }

  const storedOptions = JSON.parse(storageValue);

  // support for old format
  if (storedOptions.active) {
    return {
      ...defaultOptions,
      sort: storedOptions as Sort,
    };
  }

  return {
    ...defaultOptions,
    ...storedOptions,
  } as TOptions;
};

const registerSort = (sort: MatSort, options: WritableSignal<TableOptionsState>) => {
  const currentOptions = options();
  sort.active = currentOptions.sort.active;
  sort.direction = currentOptions.sort.direction;
  sort.sortChange.emit();
  sort.sortChange.subscribe(({ active, direction }) => options.update((o) => ({ ...o, sort: { active, direction } })));
};

const registerPaginatorPageSize = (paginator: MatPaginator, options: WritableSignal<TableOptionsState>) => {
  paginator.pageSize = options().pageSize;
  paginator.page.emit();
  paginator.page.subscribe(({ pageSize }) => options.update((o) => ({ ...o, pageSize })));
};
